/**
 * Capabilities:
 * - CAP-VOUCHER-REDEMPTION
 *
 * Responsibilities:
 * - Render member vouchers and drive the member-side redemption UX, including
 *   QR scan, option picking, daily-code fallback, and backend error mapping.
 *
 * Context:
 * - project-intelligence/capabilities/requested/member/voucher-redemption.md
 */

import React, { useState, useEffect, useRef } from 'react';

import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';

import moment from 'moment-timezone';

import ButtonPickerSequence from './ButtonPickerSequence';

import { VerticalSeparator, HorizontallyCenteringContainer } from './utils';
import { FullWidthSpinner } from '../loader-components';
import { redeemVoucher, daysElapsedSince, qrScanForTableInfo, testAdditionalVoucherPrerequisites } from './utils';

import { isDev } from '../../utils/general-utils';

import { ENABLE_REDEMPTION_FLOW_CONFIG_IN_PROD_UNCONDITIONALLY, ENABLE_REDEMPTION_FLOW_CONFIG_IN_PROD_AFTER, CARDS_ALLOWED_TO_USE_REDEMPTION_FLOW_CONFIG_IN_PROD } from './AppSettings';

const VOUCHER_NAME_TEXT_REPLACEMENTS = [
    // [/\[MOBILE\]/g, "📱"]
];

const USE_LOCATION_SPECIFIC_BLACK_BUTTONS = false;
const USE_DAILY_CODE_VERIFICATION_FOR_BLACK_BUTTONS = true;

// Responsibility index:
// - tryRedemption: Oracle-integrated or table-scan-based redemption entry
// - tryRedemptionFromWithDailyCode: fallback daily-code path
// - getChoice: transform backend option availability into frontend selection UI

export function VoucherRaw({
    voucherNumber, availableOutlets, termsAndConditions = [], remarks = '',
    textHeading = 'Voucher Name Here',
    expiryDate = new Date((new Date()).getTime() + 24 * 60 * 60 * 1000 * 365),
    imgSrc = 'https://uatcrm.ascentis.com.sg/MatrixResources/VoucherTypeImage/swensens/FreeDDrink.jpg',
    couponStyle = 'coupon-style-default',
    customRedemptionSuccessMessage = null,
    callRedeemVoucher, voucherNotes, lastRedemptionRecord, redemptionRecord,
    isRedeemable,
    redemptionDate = null,
    // voucherType,
    redemptionFlowConfig = null,
    callTestAdditionalVoucherPrerequisites,
} = {}) {
    const [showVoucherModal, setShowVoucherModal] = useState(false);
    const [isRedeeming, setIsRedeeming] = useState(false);
    const [isInCustomRedemptionFlow, setIsInCustomRedemptionFlow] = useState(false);
    const [isScanningQR, setIsScanningQR] = useState(false);
    const [isDoingChecks, setIsDoingChecks] = useState(false);
    const [isDoneRedeeming, setIsDoneRedeeming] = useState(false);
    const [redemptionMessageColor, setRedemptionMessageColor] = useState('red');
    const [redemptionMessage, setRedemptionMessage] = useState(null);
    const [redemptionOptionButtonGroups, setRedemptionOptionButtonGroups] = useState([]);
    const [redemptionOptionButtonSequenceCallback, setRedemptionOptionButtonSequenceCallback] = useState(null);

    const _termsAndConditions = Array.from(termsAndConditions);
    if (!!availableOutlets) {
        _termsAndConditions.unshift(`Available Outlets: ${availableOutlets}`)
    }

    const remarksList = remarks.split('|').filter(s => !!(s.trim()));

    // const timeStringNow = moment(new Date()).tz('Asia/Singapore').format().split('T')[1].split(':').slice(0,2).join('');

    const closeVoucherModal = () => {
        setShowVoucherModal(false);
        setIsRedeeming(false);
        setIsInCustomRedemptionFlow(false);
        setRedemptionOptionButtonGroups([]);
        setRedemptionOptionButtonSequenceCallback(null);
        setIsDoneRedeeming(false);
        setRedemptionMessage(null);
    };

    const voucherRedeemTopRef = useRef(null);

    // Scroll to top when modal is opened
    useEffect(() => {
        if (showVoucherModal) {
            setTimeout(() => {
                if (voucherRedeemTopRef?.current) {
                    window._ref = voucherRedeemTopRef?.current
                    voucherRedeemTopRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
                }
            }, 50);
        }
    }, [showVoucherModal]);

    const showExpiry = expiryDate && ((expiryDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24 * 365.25) < 5)

    const ifRedeemableFrom = (location, locationGroup, redemptionFlowConfig, { 
        openDateISO = '0000-00-00', closeDateISO = '9999-99-99'
    } = {}) => {
        const isoDate = (new Date()).toISOString().split('T')[0];
        if (isoDate < openDateISO) {
            return false;
        }
        if (isoDate >= closeDateISO) {
            return false;
        }

        if (!redemptionFlowConfig) {
            return true;
        }
        const redemptionAllowedOutletPrefixes = redemptionFlowConfig?.allowed_outlet_prefixes || [];
        const redemptionOverrideIncludedOutlets = redemptionFlowConfig?.override_included_outlets || [];
        const redemptionOverrideExcludedOutlets = redemptionFlowConfig?.override_excluded_outlets || [];
        if (redemptionOverrideIncludedOutlets.includes(location)) {
            return true;
        }
        if (redemptionOverrideExcludedOutlets.includes(location)) {
            return false;
        }
        return redemptionAllowedOutletPrefixes.includes(locationGroup);
    }

    const callRedeemVoucherWithCustomRedemptionSource = customRedemptionSource => {
        return callRedeemVoucher(null, customRedemptionSource)
            .then(({ success, transactionReferenceInfo, errorMessage }) => {
                setIsRedeeming(false);
                setIsInCustomRedemptionFlow(false);
                setRedemptionOptionButtonGroups([]);
                setRedemptionOptionButtonSequenceCallback(null);

                const successMessage = 'Please show this to a server to complete your redemption.';
                if (success) {
                    setIsDoneRedeeming(true);
                    setRedemptionMessageColor('green');
                    setRedemptionMessage(successMessage);
                } else {
                    setRedemptionMessageColor('red');
                    setRedemptionMessage(errorMessage);
                }
            })
            .catch(e => {
                console.error('Redemption Error:', e)
                setIsRedeeming(false);
                setIsInCustomRedemptionFlow(false);
                setRedemptionOptionButtonGroups([]);
                setRedemptionOptionButtonSequenceCallback(null);
        
                const failureMessage = 'Unable to redeem voucher. Try again later.';
                setRedemptionMessageColor('red');
                setRedemptionMessage(failureMessage);
            });
    }

    const tryRedemptionFrom = (location, fullLocationName) => () => {
        const msg = !!fullLocationName ? `Before beginning, please be sure to have a server with you. Go ahead and initiate redemption process (at ${fullLocationName})?` : `(Warning: Staff Only Action) This action will consume the voucher. Do not initiate this without staff assistance. Go ahead and initiate redemption process?`;
        if (!window.confirm(msg)) {
            return;
        }
        console.log(`Staring redemption flow for ${voucherNumber} from ${location}.`)

        setIsRedeeming(true);

        const customRedemptionSource = location;
        return callRedeemVoucherWithCustomRedemptionSource(customRedemptionSource)
    };
    
    const tryRedemptionFromWithDailyCode = () => {
        const msg = `(Warning: Staff Only Action) This action will consume the voucher. Do not initiate this without staff assistance. Go ahead and initiate redemption process?`;
        if (!window.confirm(msg)) {
            return;
        }
        
        // Prompt for daily code
        const dailyCode = window.prompt('Please enter the 4-digit daily code:');
        if (!dailyCode) {
            return; // User cancelled
        }
        
        // Validate 4 digits in range 1000-9999
        if (!/^\d{4}$/.test(dailyCode) || parseInt(dailyCode) < 1000 || parseInt(dailyCode) > 9999) {
            window.alert('Invalid daily code. Please enter a 4-digit code between 1000 and 9999.');
            return;
        }
        
        console.log(`Starting redemption flow for ${voucherNumber} with daily code.`)

        setIsRedeeming(true);

        const customRedemptionSource = { daily_code: dailyCode };
        return callRedeemVoucherWithCustomRedemptionSource(customRedemptionSource)
    };

    const skipTableScan = !!redemptionFlowConfig ? redemptionFlowConfig.skip_table_scan : true;
    
    const tryRedemption = () => {
        if (!window.confirm(skipTableScan ? 'Before beginning, please be sure to have a server with you. Go ahead and initiate redemption process?' : 'Go ahead and initiate redemption process?')) {
            return;
        }
        console.log(`Staring redemption flow for ${voucherNumber} with redemption flow configuration:`, redemptionFlowConfig)

        let scanQR = !!redemptionFlowConfig ? !redemptionFlowConfig.skip_table_scan : false;
        
        const getChoice = (tableInfo, availableOptionsInfo) => new Promise((resolveInner, rejectInner) => {
            const { outlet: outletCode } = tableInfo;
            const plusStockedOut = availableOptionsInfo.options_stocked_out;
            const availableOptions = availableOptionsInfo.available_options || [];
            const optionsNotAvailableHere = availableOptionsInfo.options_not_available_here || [];
            console.log('plusStockedOut:', plusStockedOut)
            console.log('availableOptions:', availableOptions)
            console.log('optionsNotAvailableHere:', availableOptions)

            const outletPrefix = outletCode.substr(0, 2);
            const outlet = outletCode.substr(2);
            const availableAtThisOutlet = (
                (
                    (redemptionFlowConfig?.allowed_outlet_prefixes || []).includes(outletPrefix)
                    &&
                    !((redemptionFlowConfig?.override_excluded_outlets || []).includes(outletPrefix))
                )
                ||
                (redemptionFlowConfig?.override_included_outlets || []).includes(outlet)
            );
            if (!availableAtThisOutlet) {
                console.log(`Redemption not available at this outlet (${outletCode}). See config.`)
                rejectInner('not-available-at-this-outlet')
                return;
            } else {
                console.log(`Redemption available at this outlet (${outletCode}).`)
            }

            setIsDoingChecks(false);

            const detailedOptionGroups = (() => {
                const groups = [];
                const groupIdx = {};
                (redemptionFlowConfig?.available_options || [])
                    .map(option => ({
                        ...option,
                        isAvailable: availableOptions.includes(option.plu),
                        isStockedOut: plusStockedOut.includes(option.plu),
                        disabled: !availableOptions.includes(option.plu) || plusStockedOut.includes(option.plu),
                    }))
                    .filter(option => !optionsNotAvailableHere.map(o => `${o.label}|${o.plu}`).includes(`${option.label}|${option.plu}`))
                    .sort((x,y) => x.disabled < y.disabled ? -1 : 1)
                    .forEach(option => {
                        (option.groups || []).forEach(group => {
                            if (!Number.isInteger(groupIdx[group])) {
                                groupIdx[group] = groups.length;
                                groups.push({
                                    type: 'menu-item',
                                    group,
                                    options: []
                                });
                            }
                            groups[groupIdx[group]].options.push(option);
                        })
                    })
                return groups;
            })();
            console.log('Detailed Options Groups:', detailedOptionGroups)

            const additionalInstructionsGroups = (() => {
                const groups = [];
                const groupIdx = {};
                (redemptionFlowConfig?.additional_instructions_options || []).forEach(option => {
                    (option.groups || []).forEach(group => {
                        if (!Number.isInteger(groupIdx[group])) {
                            groupIdx[group] = groups.length;
                            groups.push({
                                type: 'instruction',
                                group,
                                options: []
                            });
                        }
                        groups[groupIdx[group]].options.push(option);
                    })
                })
                return groups;
            })();
            console.log('Additional Instructions Groups:', additionalInstructionsGroups)

            function cleanUpGroupsInOptions(submission) {
                // remove unnecessary data (you just need the choices)
                const newSubmission = { ...submission };
                if (!!newSubmission?.choice?.groups) {
                    delete newSubmission.choice['groups'];
                }
                Object.keys(newSubmission?.choices || {}).forEach(k => {
                    const choice = newSubmission?.choices[k]
                    if (!!choice?.groups) {
                        delete choice['groups'];
                    }
                })
                return newSubmission;
            }

            const allSelectionGroups = [].concat(detailedOptionGroups, additionalInstructionsGroups)
            const haveSelectableOptions = allSelectionGroups.filter(({ options }) => options.length > 1).length > 0

            if (allSelectionGroups.length > 0) {
                const allFirstChoices = Object.fromEntries(allSelectionGroups.map(({ options, group, type }, idx) => [group, { ...options[0], pos: idx, type }]))
                if (!haveSelectableOptions) {
                    const defaultSubmission = cleanUpGroupsInOptions({
                        tableInfo,
                        // choice: { // Backward Compatibility
                        //     group: allSelectionGroups[0].group,
                        //     label: allSelectionGroups[0].options[0].label,
                        //     plu: allSelectionGroups[0].options[0].plu,
                        // },
                        choices: allFirstChoices
                    })
                    console.log('Only Single Options:', defaultSubmission)
                    resolveInner(defaultSubmission)
                } else {
                    console.log('Multiple Options')
                    const reducedChoiceList = allSelectionGroups.map(({ options, group, type }, idx) => (options.length > 1 ? {
                        // originalIdx: idx,
                        name: group,
                        group,
                        options: options.map(opt => ({ ...opt, pos: idx, type })),
                        captionText: `Please select ${
                            group === 'DEFAULT' ? 'your desired option' : (
                                group === 'SERVING_TIME' ? 'your preferred serving time' : group
                            )
                        }:`,
                        cancelText: 'CANCEL REDEMPTION',                    
                    } : null)).filter(x => !!x)
                    console.log('Actual Choice List:', reducedChoiceList)
                    setRedemptionOptionButtonGroups(reducedChoiceList);

                    // need to do this to set a function as state
                    setRedemptionOptionButtonSequenceCallback(() => choices => {
                        console.log('Received Choices:', choices)
                        if (!choices) {
                            rejectInner('no-choice-made');
                        } else {
                            const submittedChoices = { ...allFirstChoices }
                            choices.forEach((choice, idx) => {
                                submittedChoices[reducedChoiceList[idx].group] = choice
                            })

                            const submission = cleanUpGroupsInOptions({
                                tableInfo,
                                choices: submittedChoices
                            })
                            console.log('Submitting:', submission)
                            // rejectInner('not-implemented');
                            resolveInner(submission)
                        }
                    })
                }
            } else {
                resolveInner({ tableInfo });
            }
        });

        const p = (() => new Promise((resolve, reject) => {
            if (scanQR) {
                setIsInCustomRedemptionFlow(true);
                setIsScanningQR(true);
                qrScanForTableInfo()
                    .then(tableInfo => {
                        setIsScanningQR(false);
                        setIsDoingChecks(true);
                        return callTestAdditionalVoucherPrerequisites(voucherNumber, { tableInfo })
                            .then(({ success, available_options_info: availableOptionsInfo, remarks }) => {
                                if (success) {
                                    return getChoice(tableInfo, availableOptionsInfo);
                                } else {
                                    if (remarks === 'no-check') {
                                        return Promise.reject('no-check')
                                    } else if (remarks === 'have-existing-item-discount') {
                                        return Promise.reject('have-existing-item-discount')
                                    } else if (remarks === 'option-stockout') {
                                        return Promise.reject('option-stockout')
                                    } else if (remarks === 'outlet-availability') {
                                        return Promise.reject('outlet-availability')
                                    } else if (remarks === 'table-availability') {
                                        return Promise.reject('table-availability')
                                    } else {
                                        return Promise.reject('voucher-requirements-not-met')
                                    }
                                }
                            })
                            .then(r => {
                                console.log('After getting table info, verifying pre-requisites, and waiting for selection:', r)
                                return r;
                            })
                            .catch(e => {
                                setIsDoingChecks(false);
                                throw e;
                            })
                    }).then(r => {
                        resolve(r)
                    }).catch(e => {
                        setIsScanningQR(false);
                        reject(e)
                    })
            } else {
                console.log('Not checking for table information. This requires staff assistance.')
                resolve(null)
            }
        }))();
        
        p.catch(e => {
            if (e === 'qr-scan-not-supported') {
                console.log('Telegram QR Scan Not Supported.');
                throw e;
            }
            throw e;
        }).then(additionalRedemptionInformation => {
            console.log('additionalRedemptionInformation:', additionalRedemptionInformation)
            setIsRedeeming(true);
            return callRedeemVoucher(additionalRedemptionInformation)
                .then(({ success, transactionReferenceInfo, errorMessage }) => {
                    setIsRedeeming(false);
                    setIsInCustomRedemptionFlow(false);
                    setRedemptionOptionButtonGroups([]);
                    setRedemptionOptionButtonSequenceCallback(null);
                    setIsDoneRedeeming(true)
    
                    const successMessage = customRedemptionSuccessMessage || (skipTableScan ? 'Please show this to a server to complete your redemption.' : 'Successfully redeemed voucher.');
                    if (success) {
                        setRedemptionMessageColor('green');
                        setRedemptionMessage(successMessage);
                    } else {
                        setRedemptionMessageColor('red');
                        setRedemptionMessage(errorMessage);
                    }
                });
        }).catch(e => {
            console.error('Redemption Error:', e)
            setIsRedeeming(false);
            setIsInCustomRedemptionFlow(false);
            setRedemptionOptionButtonGroups([]);
            setRedemptionOptionButtonSequenceCallback(null);
    
            let failureMessage = 'Unable to redeem voucher. Try again later.';
            if (e === 'no-check') {
                failureMessage = 'Unable to redeem voucher. No valid check found. Try again later.';
            }
            if (e === 'have-existing-item-discount') {
                failureMessage = 'Voucher cannot be used in conjunction with another discount in the same transaction.';
            }
            if (e === 'option-stockout') {
                failureMessage = 'Currently not available. (Sorry!)';
            }
            if (e === 'outlet-availability') {
                failureMessage = 'Not available at this outlet. Please see the description, or terms and conditions.';
            }
            if (e === 'table-availability') {
                failureMessage = 'Please see description for redemption instructions.';
            }
            if (e === 'voucher-requirements-not-met') {
                failureMessage = 'Redemption requirements not met. Please check the terms and conditions.';
            }
            if (e === 'not-available-at-this-outlet') {
                failureMessage = 'Redemption not available at this outlet. Please check the terms and conditions.';
            }
            if (e === 'qr-scan-not-supported') {
                failureMessage = 'Unable to redeem voucher on this device. Please approach staff for assistace.';
            }
            if (e === 'invalid-qr-data') {
                failureMessage = 'Please scan the QR Code on your table\'s Standee.';
            }
            if (e !== 'no-choice-made' && e !== 'qr-scan-timed-out') {
                setIsDoneRedeeming(true)
                setRedemptionMessageColor('red');
                setRedemptionMessage(failureMessage);
            }
        });
    };

    return (
        <>
            <VerticalSeparator height='3px'/>
            <div className={`coupon ${couponStyle}`} onClick={() => setShowVoucherModal(true)}>
                <img alt={textHeading} src={imgSrc}/>
                <div className='coupon-text'>
                    <div className='coupon-text-heading'>{textHeading}</div>
                    {
                        !!redemptionDate ? (
                            <div className='coupon-text-expiry'>
                                Redeemed on {moment(redemptionDate).format('DD MMM YYYY, h:mm a')}
                            </div>
                        ) : (
                            showExpiry && (
                                <div className='coupon-text-expiry'>
                                    Valid till {moment(expiryDate).format('DD MMMM YYYY')}
                                </div>
                            )
                        )
                    }
                </div>
            </div>
            <VerticalSeparator height='3px'/>
            <Modal
                show={showVoucherModal} fullscreen={true}
                onHide={closeVoucherModal}
            >
                <div style={{
                    height: 'calc(20px)',
                    backgroundColor: 'var(--app-top-strip-color)'
                }}/>
                <div>
                    <img className="w100" alt={textHeading} src={imgSrc}/>
                </div>
                <Modal.Header closeButton>
                    <span className="voucher-redeem-header">{textHeading}</span>
                </Modal.Header>
                <Modal.Body>
                    <div ref={voucherRedeemTopRef} style={{ height: '0px' }}/>
                    {
                        !isRedeeming && (
                            <>
                                {
                                    !isDoneRedeeming && isRedeemable && !redemptionDate ? (
                                        (
                                            !isInCustomRedemptionFlow ? (
                                                <HorizontallyCenteringContainer>
                                                    <Button
                                                        className="w80 swensens-button"
                                                        onClick={tryRedemption}
                                                    >REDEEM</Button>
                                                </HorizontallyCenteringContainer>
                                            ) : (
                                                (isDoingChecks || isScanningQR) ? (
                                                    <HorizontallyCenteringContainer>
                                                        Please wait...
                                                    </HorizontallyCenteringContainer>
                                                ) : (
                                                    !!redemptionOptionButtonSequenceCallback && (
                                                        <>
                                                            <ButtonPickerSequence
                                                                buttonPickerGroups={redemptionOptionButtonGroups}
                                                                sequenceCompletionCallback={redemptionOptionButtonSequenceCallback}
                                                            />
                                                        </>
                                                    )
                                                )
                                            )
                                        )
                                    ) : (
                                        <HorizontallyCenteringContainer>
                                            <Button
                                                className="w80 swensens-button"
                                                onClick={closeVoucherModal}
                                            >OK</Button>
                                        </HorizontallyCenteringContainer>
                                    )
                                }
                                {
                                    voucherNotes && (
                                        <HorizontallyCenteringContainer>
                                            <div style={{ fontSize: '0.7em' }}>
                                                {voucherNotes}
                                            </div>
                                        </HorizontallyCenteringContainer>
                                    )
                                }
                                {
                                    lastRedemptionRecord?.ts && (
                                        <>
                                            <HorizontallyCenteringContainer>
                                                <div style={{ fontSize: '0.7em' }}>
                                                    Last Redemption: {moment(lastRedemptionRecord.ts).format('DD MMM YYYY (ddd), h:mm a')}{lastRedemptionRecord?.data?.customRedemptionSource && ` (${lastRedemptionRecord?.data?.customRedemptionSource})`}
                                                </div>
                                            </HorizontallyCenteringContainer>
                                        </>
                                    )
                                }
                                {
                                    redemptionRecord?.ts && (
                                        <>
                                            <HorizontallyCenteringContainer>
                                                <div style={{ fontSize: '0.7em' }}>
                                                    Redeemed at: {moment(redemptionRecord.ts).format('DD MMM YYYY (ddd), h:mm a')}{redemptionRecord?.data?.customRedemptionSource && ` (${redemptionRecord?.data?.customRedemptionSource})`}
                                                </div>
                                            </HorizontallyCenteringContainer>
                                            {
                                                redemptionRecord?.data?.additionalRedemptionInformation && (() => {
                                                    const outlet = redemptionRecord?.data?.additionalRedemptionInformation?.tableInfo?.outlet;
                                                    const table = redemptionRecord?.data?.additionalRedemptionInformation?.tableInfo?.table;
                                                    const choices = redemptionRecord?.data?.additionalRedemptionInformation?.choices || [];
                                                    const choiceName = (() => {
                                                        if (Object.keys(choices).length === 0) {
                                                            return null;
                                                        } else if (Object.keys(choices).length === 1) {
                                                            return choices[Object.keys(choices)[0]].label;
                                                        } else {
                                                            if (!!choices['DEFAULT']) {
                                                                return choices['DEFAULT'].label + ', ...';
                                                            } else {
                                                                return choices[Object.keys(choices)[0]].label + ', ...';
                                                            }
                                                        }
                                                    })();
                                                    const checkNumber = redemptionRecord?.data?.additionalRedemptionInformation?.check_info?.checkNumber || '-';
                                                    return (
                                                        <>
                                                            {
                                                                !!choiceName && (
                                                                    <HorizontallyCenteringContainer>
                                                                        <div style={{ fontSize: '0.7em' }}>
                                                                            Selection: {choiceName}
                                                                        </div>
                                                                    </HorizontallyCenteringContainer>
                                                                )
                                                            }
                                                            <HorizontallyCenteringContainer>
                                                                <div style={{ fontSize: '0.7em' }}>
                                                                    ({outlet}, table {table}, check {checkNumber})
                                                                </div>
                                                            </HorizontallyCenteringContainer>
                                                        </>
                                                    );
                                                })()
                                            }
                                            {
                                                !redemptionFlowConfig && (daysElapsedSince(redemptionRecord.ts) < 1.0 / 24) && (
                                                    <HorizontallyCenteringContainer>
                                                        <div style={{ fontSize: '0.7em' }}>
                                                            Be sure to show this to a server to complete your redemption.
                                                        </div>
                                                    </HorizontallyCenteringContainer>
                                                )
                                            }
                                        </>
                                    )
                                }
                                {
                                    redemptionMessage && (
                                        <HorizontallyCenteringContainer>
                                            <div style={{ color: redemptionMessageColor, fontSize: '0.9em' }}>
                                                {redemptionMessage}
                                            </div>
                                        </HorizontallyCenteringContainer>
                                    )
                                }
                                <VerticalSeparator height='1.5em'/>
                            </>
                        )
                    }
                    {
                        !isRedeeming && (remarksList.length > 0) && (
                            remarksList.map((remarks, idx) => (
                                <div key={`remarks-${idx}`}className="voucher-redeem-remarks">{remarks}</div>
                            ))
                        )
                    }
                    {
                        !isRedeeming && showExpiry && (
                            <>
                                <VerticalSeparator height='0.5em'/>
                                <div className="voucher-redeem-expiry">Valid till: {moment(expiryDate).format('DD MMMM YYYY')}</div>
                                <VerticalSeparator height='1.5em'/>
                            </>
                        )
                    }
                    {
                        !isRedeeming ? (
                            <>
                                {/* {
                                    !!availableOutlets && (
                                        <div style={{ fontSize: '0.7em' }}>
                                            <p><strong>Available Outlets</strong>: {availableOutlets}</p>
                                        </div>
                                    )
                                }
                                {
                                    termsAndConditions.length > 0 && (
                                        <div style={{ fontSize: '0.7em' }}>
                                            <p>T&Cs apply:</p>
                                            <ul>
                                                {termsAndConditions.map((line, idx) => (
                                                    <li key={`termsAndConditions-${idx}`}>{line}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )
                                } */}
                                {
                                    _termsAndConditions.length > 0 && (
                                        <div style={{ fontSize: '0.7em' }}>
                                            <p>T&Cs apply:</p>
                                            <ul>
                                                {_termsAndConditions.map((line, idx) => (
                                                    <li key={`termsAndConditions-${idx}`}>{line}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )
                                }
                                <VerticalSeparator height='0.3em'/>
                                <div className="voucher-redeem-remarks">
                                    When experiencing redemption issues, please ask for assistance. Our staff will be on hand to help.
                                </div>
                                <VerticalSeparator height='1.5em'/>
                            </>
                        ) : (
                            !isDoneRedeeming && (
                                <div style={{
                                    position: 'absolute',
                                    left: '0px',
                                    width: '100vw',
                                }}>
                                    <FullWidthSpinner
                                        text='Redeeming...'
                                        heightInVH={40}
                                        scale={3} />
                                </div>
                            )
                        )
                    }
                    {
                        !isRedeeming && !isScanningQR && !isDoingChecks && !skipTableScan && (
                            <>
                                {
                                    !isDoneRedeeming && isRedeemable && !redemptionDate && (
                                        <>
                                            <VerticalSeparator height='1em'/>
                                            <hr/>
                                            {
                                                USE_LOCATION_SPECIFIC_BLACK_BUTTONS && (
                                                    <>
                                                        {
                                                            ifRedeemableFrom('BPP', 'SW', redemptionFlowConfig) && (
                                                                <>
                                                                    <VerticalSeparator height='1em'/>
                                                                    <HorizontallyCenteringContainer>
                                                                        <div style={{ fontSize: '0.7em' }}>
                                                                        For redemptions from Bukit Panjang Plaza use the respective button below.
                                                                        </div>
                                                                    </HorizontallyCenteringContainer>
                                                                    <VerticalSeparator height='1em'/>
                                                                    <HorizontallyCenteringContainer>
                                                                        <Button
                                                                            className="w80 swensens-black-button"
                                                                            onClick={tryRedemptionFrom('BPP', 'Bukit Panjang Plaza')}
                                                                        >REDEEM from Bukit Panjang Plaza</Button>
                                                                    </HorizontallyCenteringContainer>
                                                                    <VerticalSeparator height='0.25em'/>
                                                                </>
                                                            )
                                                        }
                                                        {
                                                            ifRedeemableFrom('T3', 'SW', redemptionFlowConfig) && (
                                                                <>
                                                                    <HorizontallyCenteringContainer>
                                                                        <Button
                                                                            className="w80 swensens-black-button"
                                                                            onClick={tryRedemptionFrom('T3', 'Changi Airport T3')}
                                                                        >REDEEM from Changi Airport T3</Button>
                                                                    </HorizontallyCenteringContainer>
                                                                    <VerticalSeparator height='0.25em'/>
                                                                </>
                                                            )
                                                        }
                                                        {
                                                            ifRedeemableFrom('VCT', 'ES', redemptionFlowConfig, { openDateISO: '2025-01-01' }) && (
                                                                <>
                                                                    <HorizontallyCenteringContainer>
                                                                        <Button
                                                                            className="w80 swensens-black-button"
                                                                            onClick={tryRedemptionFrom('VCT', 'Vivocity')}
                                                                        >REDEEM from Vivocity</Button>
                                                                    </HorizontallyCenteringContainer>
                                                                    <VerticalSeparator height='0.25em'/>
                                                                </>
                                                            )
                                                        }
                                                    </>
                                                )
                                            }
                                            <>
                                                <HorizontallyCenteringContainer>
                                                    {
                                                        USE_DAILY_CODE_VERIFICATION_FOR_BLACK_BUTTONS ? (
                                                            <>
                                                                <Button
                                                                    className="w80 swensens-black-button"
                                                                    onClick={tryRedemptionFromWithDailyCode}
                                                                    disabled={isRedeeming}
                                                                >REDEEM (Staff Use Only)</Button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Button
                                                                    className="w80 swensens-black-button"
                                                                    onClick={tryRedemptionFrom('SW/ES', '')}
                                                                >REDEEM (Staff Use Only)</Button>
                                                            </>
                                                        )
                                                    }
                                                </HorizontallyCenteringContainer>
                                                <VerticalSeparator height='0.25em'/>
                                            </>
                                        </>
                                    )
                                }
                                <VerticalSeparator height='1.5em'/>
                            </>
                        )
                    }
                    <VerticalSeparator height='80vw'/>
                </Modal.Body>
            </Modal>
        </>
    )
}

export function getExpiryForAdditionalVoucher(voucher, memberData) {
    let expiryDate = new Date(new Date(voucher.expiryYear, voucher.expiryMonth - 1, voucher.expiryDay).getTime() + 1000 * 60 * 60 * 24 - 1);

    if (!!voucher.is_personal) {
        const expiryTs = voucher?.personal_voucher_data?.parsed_data?.expires || 100000000000;
        const expiryRaw = new Date(1000 * expiryTs)
        const expiryRounded =  new Date((new Date(expiryRaw.getFullYear(), expiryRaw.getMonth(), expiryRaw.getDate() + 1)).getTime() - 1)
        if (expiryRounded < expiryDate) {
            expiryDate = expiryRounded
        }
    }

    (voucher.rules || []).forEach(r => {
        // if (r.type === 'within_duration_of_first_sign_up') {
        //     const expiryRaw = new Date(1000 * ((memberData?.__member_record__?.first_link_ts || memberData?.__member_record__?.link_ts || 0) + r.max_days * 24 * 60 * 60))
        //     const expiryRounded =  new Date((new Date(expiryRaw.getFullYear(), expiryRaw.getMonth(), expiryRaw.getDate() + 1)).getTime() - 1)
        //     if (expiryRounded < expiryDate) {
        //         expiryDate = expiryRounded
        //     }
        // }

        // if (r.type === 'in_birthday_month' || r.type === 'in_birthday_month_or_preceeding_month') {
        // const dob = parseDateTimestampString(memberData?.MemberInfo?.DOB || '/Date(0)/')
        //     const now = new Date()
        //     let endOfBirthdayMonthThisYear = new Date((new Date(now.getFullYear(), dob.getMonth() + 1, 1)).getTime() - 1)
        //     let endOfBirthdayMonthNextYear = new Date((new Date(now.getFullYear() + 1, dob.getMonth() + 1, 1)).getTime() - 1)
        //     const expiryRounded = Math.abs(endOfBirthdayMonthThisYear - now) < Math.abs(endOfBirthdayMonthNextYear - now) ? endOfBirthdayMonthThisYear : endOfBirthdayMonthNextYear;
        //     if (expiryRounded < expiryDate) {
        //         expiryDate = expiryRounded
        //     }
        // }

        if (voucher?.__info__?.update_expiry) {
            const updatedExpiry = new Date(voucher?.__info__?.update_expiry)
            if (updatedExpiry < expiryDate) {
                expiryDate = updatedExpiry
            }
        }
    })
    return expiryDate;
}

export default function Voucher({
    voucher, voucherType = 'standard', token,
    ownCardNumber = null, childCardNumber = null,
    updateMemberData, lastRedemptionRecord, redemptionRecord, redemptionDate,
    memberData
}) {
    const CURRENT_MEMBER_USES_REDEMPTION_FLOW_CONFIG_IN_PROD = (
        CARDS_ALLOWED_TO_USE_REDEMPTION_FLOW_CONFIG_IN_PROD.includes((memberData?.CardInfo?.CardNo || '---').toString())
        ||
        CARDS_ALLOWED_TO_USE_REDEMPTION_FLOW_CONFIG_IN_PROD.includes((childCardNumber || '---').toString())
    );

    const voucherNumber = voucherType === 'additional' ? voucher.vid : voucher.VoucherNo;
    // const vid = voucherType === 'additional' ? voucher.vid : null;

    // const voucherOwnerCardNumber = childCardNumber || ownCardNumber;

    const isRedeemable = voucherType === 'additional' ? true : (voucher.VoucherTypeDescription || '').trim().startsWith('[MOBILE] ');

    let textHeading = voucherType === 'additional' ? (voucher.name || 'Redeemed Voucher') : (voucher.VoucherTypeDescription || '').trim();

    VOUCHER_NAME_TEXT_REPLACEMENTS.forEach(([re, replacementText]) => {
        textHeading = textHeading.replace(re, replacementText);
    })
    while (textHeading.length > 80) {
        const splt = textHeading.split(' ');
        splt.pop()
        textHeading = splt.join(' ') + '...';
    }

    let expiryDate = voucherType === 'additional' ? getExpiryForAdditionalVoucher(voucher, memberData) : new Date(Number((voucher?.ValidTo || '/Date(0)/').match(/\/Date\(([0-9]+)\)\//)[1]));

    const availableOutlets = voucherType === 'additional' ? voucher.available_outlets : (voucher?.Ref2 || '').slice(18)
    const imgSrc = voucherType === 'additional' ? voucher.image_src : (voucher?.Ref7 || '').slice(18)

    const allRemarks = voucherType === 'additional' ? [] : (voucher?.Remarks || '').split('\n').map(s => s.trim())
    const termsAndConditions = voucherType === 'additional' ? (voucher.terms_and_conditions || []) : allRemarks.filter(s => s.startsWith('- ')).map(s => s.slice(2));
    const remarks = voucherType === 'additional' ? (voucher.description || '').split('\n').filter(s => !!(s.trim())).join(' ') : allRemarks.filter(s => !s.startsWith('- ')).join(' ').trim();

    let preventRedemption = false;
    const voucherNotesList = [];
    if (!!voucher?.__info__?.failed_data_or_time_dependent_rule) {
        voucherNotesList.push('Unable to redeem voucher now. See T&Cs for details.')
        preventRedemption = true;
    }
    const voucherNotes = voucherNotesList.length > 0 ? voucherNotesList.join('  ') : null;
    const couponStyle = (() => {
        if (voucherType === 'additional') {
            if ((voucher?.redemption_flow_cfg?.allowed_outlet_prefixes || []).includes('SW')) {
                return 'coupon-style-default';
            }
            if ((voucher?.redemption_flow_cfg?.allowed_outlet_prefixes || []).includes('ES') || (voucher?.redemption_flow_cfg?.override_included_outlets || []).length > 0) {
                return 'coupon-style-alt1';
            }
            if ((voucher?.redemption_flow_cfg?.allowed_outlet_prefixes || []).length === 0 && (voucher?.redemption_flow_cfg?.override_included_outlets || []).length === 0) {
                return 'coupon-style-alt2';
            }
        }
        return 'coupon-style-default';
    })();

    return VoucherRaw({
        voucherNumber, textHeading, expiryDate, imgSrc, availableOutlets, termsAndConditions, remarks,
        couponStyle,
        voucherNotes,
        lastRedemptionRecord, redemptionRecord, redemptionDate, isRedeemable: isRedeemable && !preventRedemption, voucherType,
        redemptionFlowConfig: (
            isDev || ENABLE_REDEMPTION_FLOW_CONFIG_IN_PROD_UNCONDITIONALLY || CURRENT_MEMBER_USES_REDEMPTION_FLOW_CONFIG_IN_PROD ||
            (new Date() > ENABLE_REDEMPTION_FLOW_CONFIG_IN_PROD_AFTER)
        ) && voucher?.redemption_flow_cfg,
        customRedemptionSuccessMessage: voucher?.custom_message_redemption_success,
        callTestAdditionalVoucherPrerequisites: (vid, additionalRedemptionInformation) => {
            return testAdditionalVoucherPrerequisites({ token, vid, childCardNumber, additionalRedemptionInformation })
                .then(({ data }) => {
                    console.log('testAdditionalVoucherPrerequisites:', data)
                    return data;
                })
        },
        callRedeemVoucher: (additionalRedemptionInformation = null, customRedemptionSource = null) => {
            return redeemVoucher({ voucherNumber, voucherType, token, childCardNumber, additionalRedemptionInformation, customRedemptionSource })
                .then(({ data: { success, transactionReferenceInfo, voucherRejectionLists, errorMessage: backendErrorMessage } }) => {
                    console.log('redeemVoucher:', { success, transactionReferenceInfo, voucherRejectionLists, backendErrorMessage })
                    if (success) {
                        console.log('Voucher Redeemed:', transactionReferenceInfo)
                        if (!!updateMemberData) {
                            console.log('Updating member data...')
                            updateMemberData();
                        }
                        return { success, transactionReferenceInfo };
                    } else {
                        // const voucherRejectionMessage = voucherRejectionLists?.[0]?.Reason;
                        // const errorMessage = voucherRejectionMessage && `Unable to redeem voucher. (${voucherRejectionMessage})`;
                        const errorMessage = backendErrorMessage || 'Unable to redeem voucher.';
                        console.log('Failed to Redeem Voucher:', errorMessage)
                        return { success, transactionReferenceInfo, errorMessage };
                    }
                })
        },
    });
}
